import { useEffect } from "react";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { queryKeys } from "@utils/Query";
import { GetPortalUserResponse, userApi } from "@queries/User";
import { setUserId, setUserLanguage, setUserPermissions } from "@redux/System";
import { LANG, VIEWER_PERMISSIONS } from "@resources/Constants";
import { useAppDispatch } from "@configs/Redux";
import { broadcastLanguageChange } from "@utils/LanguageSync";

export default function useInitialUserInfo(
  orgId: string,
  options?: UseQueryOptions<GetPortalUserResponse>
) {
  const dispatch = useAppDispatch();

  const { data, isLoading, isError } = useQuery<GetPortalUserResponse>(
    [queryKeys("userInfo").getAll(), orgId],
    () => {
      return userApi.getUserWithPermissions(orgId ?? "");
    },
    {
      ...options,
      enabled: !!orgId
    }
  );

  useEffect(() => {
    dispatch(setUserPermissions(data?.permissions || VIEWER_PERMISSIONS));
    if (data?.accountLanguage) {
      const lang = data?.accountLanguage?.split("_")[0];
      const formattedLang = lang
        ? lang.charAt(0).toUpperCase() + lang.slice(1).toLowerCase()
        : LANG.EN;
      dispatch(
        setUserLanguage(formattedLang as (typeof LANG)[keyof typeof LANG])
      );
      localStorage.setItem("locale_language", formattedLang);
      broadcastLanguageChange(formattedLang);
    }
    dispatch(
      setUserId({
        userId: data?.userId || "",
        email: data?.email || ""
      })
    );
  }, [data]);

  return {
    data,
    isLoading,
    isError
  };
}
